import { createGetRequest } from 'app/selectors';
import type { IRequestState } from 'features/request/requestSlice';
import { useRequest } from 'features/request/useRequest';
import type { RatioRowData, UerRatioCollectionQuery } from 'gql/graphql';
import { RatioTypeEnum, SortEnumType } from 'gql/graphql';
import { useAgGridDataSource } from 'hooks/useAgGridDataSource';
import { useProjectData } from 'hooks/useProjectData';

import type { AgGridReact } from 'ag-grid-react';
import * as R from 'ramda';
import type { RefObject } from 'react';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useParams } from 'react-router-dom';

import { ratioCollectionGql } from '../uerCoefficientsQuery';
import type { IValueSelect } from '../UerCoefficientsQuote';

export const getWhere = (filters: Record<string, string[]>) =>
    Object.keys(filters).reduce((where: Record<string, { or: { eq: string }[] }>, fieldName) => {
        const checkedValues = filters[fieldName];

        if (checkedValues?.length) {
            where[fieldName] = {
                or: checkedValues.map((item: string) => ({
                    eq: item,
                })),
            };
        }

        return where;
    }, {});

export interface IUseUerRatioCoefficientsData {
    ratioType: RatioTypeEnum;
    currentDebounceValue: string;
    filters: Record<string, any>;
    currentValueSelect: IValueSelect | undefined;
    gridRef: RefObject<AgGridReact<RatioRowData>>;
}

export const useUerRatioCoefficientsQuoteData = (props: IUseUerRatioCoefficientsData) => {
    const { ratioType, currentDebounceValue, filters, currentValueSelect, gridRef } = props;
    const { id: projectId } = useParams();
    const { projectData } = useProjectData(projectId);
    const dispatch = useDispatch();
    const currentTabFilter = filters[ratioType];

    const [selectStateItem, setSelectStateItem] = useState({
        [RatioTypeEnum.ConstraintCoef]: [],
        [RatioTypeEnum.DismantlingCoef]: [],
    });

    const contractIdObj = useMemo(
        () => ({
            contractId: projectData?.contractId,
        }),
        [projectData?.contractId],
    );

    const [uerPriceListRatioData, , , makeRequestPriceListRatio] = useRequest({
        requestId: `uerPriceListRatio_${ratioType}`,
        query: ratioCollectionGql,
        withEffect: false,
    });

    const ratioCollectionDataFromRedux = useSelector<{}, IRequestState<UerRatioCollectionQuery>>(
        createGetRequest(`uerPriceListRatio_${ratioType}`),
    );

    const groupedData = useMemo(() => {
        const items = uerPriceListRatioData?.uerRatioCollection?.items;

        if ([RatioTypeEnum.ConstraintCoef, RatioTypeEnum.DismantlingCoef].includes(ratioType)) {
            const groups = R.uniqBy((i: any) => i?.groupId, items || []).map((i) => ({
                id: i.groupId,
                groupName: i.groupName,
                subGroupId: i?.subGroupId,
            }));

            return groups;
        }

        return [];
    }, [ratioType, uerPriceListRatioData?.uerRatioCollection?.items]);

    const selectItems =
        groupedData?.map((item) => ({
            label: item?.groupName,
            id: item?.id,
            subGroupId: !!item?.subGroupId,
        })) || [];

    const uniqSelectItems = R.uniqBy((i) => i?.label, selectItems);
    const selectedItemsData = uniqSelectItems.filter((i) => i.id === currentValueSelect?.id);

    const isWorkConditionsHasSubGroupId = selectedItemsData[0]?.subGroupId;

    const sortData = useMemo(() => {
        const currentRowData = (ratioCollectionDataFromRedux?.data?.uerRatioCollection?.items || []).filter(
            (item) => item?.groupName === currentValueSelect?.label,
        );

        if (ratioType !== RatioTypeEnum.TechPartCoef) {
            return currentValueSelect !== undefined ? currentRowData : [];
        }

        return ratioCollectionDataFromRedux?.data?.uerRatioCollection?.items || [];
    }, [ratioCollectionDataFromRedux?.data?.uerRatioCollection?.items, ratioType, currentValueSelect]);

    const refreshPriceListData = useCallback(() => {
        if (ratioType === RatioTypeEnum.TechPartCoef) {
            gridRef?.current?.api?.refreshInfiniteCache();
        }
    }, [gridRef, ratioType]);

    const sortDataUpdateGroupRow = useMemo(() => {
        const titleRow = {
            workConditions: sortData[0]?.groupName,
            code: sortData[0]?.groupName,
            id: sortData[0]?.groupId,
            groupRow: true,
        };

        if (uerPriceListRatioData?.uerRatioCollection?.items?.length === 0 || sortData?.length === 0) {
            return sortData;
        }

        return ratioType !== RatioTypeEnum.TechPartCoef && currentValueSelect !== undefined
            ? [titleRow, ...sortData]
            : sortData;
    }, [sortData, uerPriceListRatioData?.uerRatioCollection?.items?.length, ratioType, currentValueSelect]);

    const sortCount = useMemo(() => {
        if (currentValueSelect === undefined && ratioType !== RatioTypeEnum.TechPartCoef) {
            return 0;
        }

        if (ratioType === RatioTypeEnum.TechPartCoef) {
            return uerPriceListRatioData?.uerRatioCollection?.totalCount;
        }

        if (currentValueSelect !== undefined) {
            return sortDataUpdateGroupRow.length;
        }

        return 0;
    }, [
        currentValueSelect,
        ratioType,
        uerPriceListRatioData?.uerRatioCollection?.totalCount,
        sortDataUpdateGroupRow.length,
    ]);

    const where = useMemo(() => {
        const search = currentDebounceValue ? { containsi: currentDebounceValue } : undefined;
        const ratioAndGroup = [RatioTypeEnum.ConstraintCoef, RatioTypeEnum.DismantlingCoef].includes(ratioType)
            ? [{ groupIsArtificial: { neq: true } }]
            : [];

        const whereData = {
            ratioType: {
                eq: ratioType,
            },
            ...(search
                ? {
                      and: [{ or: [{ code: search }, { workConditions: search }] }, ...ratioAndGroup],
                  }
                : (ratioAndGroup.length && { and: ratioAndGroup }) || {}),
            ...getWhere(currentTabFilter),
        };

        return whereData;
    }, [currentDebounceValue, ratioType, currentTabFilter]);

    const variables = useMemo(
        () => ({
            take: ratioType === RatioTypeEnum.TechPartCoef ? 50 : 100,
            contractId: contractIdObj.contractId,
            order: [{ ratioType: SortEnumType.Asc }, { number: SortEnumType.Asc }],
            where,
        }),
        [contractIdObj.contractId, ratioType, where],
    );

    const { dataSource, getRowParams } = useAgGridDataSource({
        requestId: `uerPriceListRatio${ratioType}`,
        requestFn: makeRequestPriceListRatio,
        requestVariables: variables,
    });

    useEffect(() => {
        if (uerPriceListRatioData?.uerRatioCollection?.items && getRowParams?.current) {
            if (Array.isArray(sortData)) {
                getRowParams?.current?.successCallback(sortDataUpdateGroupRow, sortCount);
            } else {
                getRowParams?.current?.successCallback([], 0);
            }
        }
    }, [
        getRowParams,
        gridRef,
        refreshPriceListData,
        sortCount,
        sortData,
        sortDataUpdateGroupRow,
        uerPriceListRatioData?.uerRatioCollection?.items,
    ]);

    useEffect(() => {
        refreshPriceListData();
    }, [refreshPriceListData, sortCount]);

    const onMakeRequestPriceListRatio = useCallback(() => {
        dispatch(makeRequestPriceListRatio({ ...variables, where }));
    }, [dispatch, makeRequestPriceListRatio, variables, where]);

    useEffect(() => {
        if (ratioType !== RatioTypeEnum.TechPartCoef) {
            onMakeRequestPriceListRatio();
        }
    }, [onMakeRequestPriceListRatio, ratioType]);

    const currentSelectItem = useMemo(
        () => (ratioType !== RatioTypeEnum.TechPartCoef ? selectStateItem[ratioType] : []),
        [ratioType, selectStateItem],
    );

    useEffect(() => {
        if (ratioType !== RatioTypeEnum.TechPartCoef) {
            if (selectStateItem[ratioType]?.length === 0 && uniqSelectItems.length !== 0) {
                setSelectStateItem((prev) => ({
                    ...prev,
                    [ratioType]: uniqSelectItems,
                }));
            }
        }
    }, [ratioType, selectStateItem, uniqSelectItems]);

    return {
        contractIdObj,
        datasource: dataSource,
        uerPriceListRatioData,
        hasCurrentData: !!sortData.length,
        selectItems: currentSelectItem,
        selectedItemsData,
        dataItems: sortDataUpdateGroupRow,
        isWorkConditionsHasSubGroupId,
    };
};
